import { createChipCanvas } from "./chipTexture";
import * as D from "./dimensions";
import { POCKET_PAINT } from "./geometry";
import { PALETTE, Rgb, css, mix } from "./palette";
import { CAMERA_ELEVATION, KEY_LIGHT, SceneState, WheelRenderer, cameraFor } from "./scene";
import { SLOT_COUNT, TAU, WheelLayout, pocketTone, slotAngle } from "./wheelLayout";

// The same wheel, painted with a 2D context, for anything that can't give us a
// WebGPU device — WebGPU only reached Safari in 2025, so on a phone that is
// still a real share of the loge.
//
// It is not a software rasteriser: the wheel is a disc, and a disc seen from a
// fixed angle projects to an ellipse, which the canvas can draw directly. That
// keeps this to arcs and gradients while every angle on screen — which pocket
// is where, where the ball is — comes out of exactly the same state and the
// same camera the 3D version uses, so the two are the same wheel.

const SIN = Math.sin(CAMERA_ELEVATION);
const COS = Math.cos(CAMERA_ELEVATION);
/** How many staves the bowl is glued up from — the same count the shader uses. */
const STAVES = 18;
/** The side of one repeat of the cloth, in device pixels. */
const FELT_TILE = 128;
/** How tall the wheel stands on the cloth, which is how far it throws. */
const WHEEL_HEIGHT = D.bowlSurfaceY(D.R_RIM) - D.BASE_Y;

export function createCanvasRenderer(
  canvas: HTMLCanvasElement,
  layout: WheelLayout,
): WheelRenderer | null {
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // The chip artwork is the same canvas the GPU path uploads as a texture.
  const chips: HTMLCanvasElement[] = layout.entries.map((entry) =>
    createChipCanvas({ emoji: entry.emoji, kind: entry.kind, label: entry.label }, 160),
  );

  let width = 0;
  let height = 0;
  /** The table, painted once per size — see `buildFelt`. */
  let feltLayer: HTMLCanvasElement | null = null;
  let scale = 1;
  let cx = 0;
  let cy = 0;

  /** Screen position of a point at radius `r`, angle `a`, height `y`. */
  const project = (r: number, a: number, y: number): [number, number] => [
    cx + scale * r * Math.cos(a),
    cy + scale * (r * Math.sin(a) * SIN - y * COS),
  ];
  /** Screen centre of the circle of constant height `y`. */
  const centreAt = (y: number) => cy - scale * y * COS;
  const arc = (r: number, y: number, from: number, to: number, ccw = false) =>
    ctx.ellipse(cx, centreAt(y), scale * r, scale * r * SIN, 0, from, to, ccw);
  const ring = (r: number, y: number) => {
    ctx.beginPath();
    arc(r, y, 0, TAU);
    ctx.closePath();
  };

  /** A lit disc: light gathering up and to the left, as everywhere else. */
  const litFill = (r: number, y: number, light: Rgb, dark: Rgb) => {
    const centre = centreAt(y);
    const gradient = ctx.createRadialGradient(
      cx - scale * r * 0.35,
      centre - scale * r * SIN * 0.5,
      scale * r * 0.1,
      cx,
      centre,
      scale * r * 1.15,
    );
    gradient.addColorStop(0, css(light));
    gradient.addColorStop(1, css(dark));
    return gradient;
  };

  /**
   * The wood, over the ring between two radii. The 3D wheel gets its grain out
   * of the shader; there is no shader here, so the same two ideas are drawn
   * instead — a tone per stave, and fine rings across them — which is enough
   * for the bowl to read as timber rather than as brown plastic.
   */
  const grain = (rOuter: number, yOuter: number, rInner: number, yInner: number) => {
    ctx.save();
    ctx.beginPath();
    arc(rOuter, yOuter, 0, TAU);
    arc(rInner, yInner, 0, TAU);
    ctx.clip("evenodd");

    for (let i = 0; i < STAVES; i++) {
      // Deterministic, and the same sort of spread the shader's hash gives.
      const tone = ((Math.sin(i * 12.9898) * 43758.5453) % 1 + 1) % 1;
      ctx.fillStyle = `rgba(${tone > 0.5 ? "255, 236, 210" : "24, 10, 4"}, ${
        0.015 + 0.035 * Math.abs(tone - 0.5)
      })`;
      ctx.beginPath();
      ctx.moveTo(cx, centreAt(yOuter));
      arc(rOuter, yOuter, (i / STAVES) * TAU, ((i + 1) / STAVES) * TAU);
      ctx.closePath();
      ctx.fill();
    }

    ctx.lineWidth = Math.max(1, scale * 0.004);
    for (let i = 0; i < 26; i++) {
      const t = i / 25;
      const r = rInner + (rOuter - rInner) * t;
      const y = yInner + (yOuter - yInner) * t;
      ctx.strokeStyle = `rgba(${i % 2 ? "255, 232, 205" : "20, 8, 3"}, 0.045)`;
      ctx.beginPath();
      arc(r, y, 0, TAU);
      ctx.stroke();
    }
    ctx.restore();
  };

  /**
   * One patch of baize, small enough to tile and fine enough that the tiling
   * doesn't show. Painted as a brightness to lay over the cloth's colour:
   * white where the weave and the fibres catch the light, black where they
   * don't, with the alpha carrying how much.
   *
   * The weave's period divides the tile exactly, so the threads join up with
   * themselves across the seam rather than stopping dead at it.
   */
  const buildFeltTile = (): HTMLCanvasElement | null => {
    const tile = document.createElement("canvas");
    tile.width = FELT_TILE;
    tile.height = FELT_TILE;
    const paint = tile.getContext("2d");
    if (!paint) return null;
    const image = paint.createImageData(FELT_TILE, FELT_TILE);
    let seed = 0x9e3779b9;
    const random = () => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 4294967296;
    };
    for (let y = 0; y < FELT_TILE; y++) {
      for (let x = 0; x < FELT_TILE; x++) {
        const weave =
          Math.sin((x / FELT_TILE) * TAU * 22) * Math.sin((y / FELT_TILE) * TAU * 22);
        const value = weave * 14 + (random() - 0.5) * 54;
        const i = (y * FELT_TILE + x) * 4;
        const lit = value > 0;
        image.data[i] = lit ? 255 : 0;
        image.data[i + 1] = lit ? 250 : 12;
        image.data[i + 2] = lit ? 225 : 6;
        image.data[i + 3] = Math.min(255, Math.abs(value));
      }
    }
    paint.putImageData(image, 0, 0);
    return tile;
  };

  /**
   * The whole table, painted once per resize rather than per frame: the light
   * across it and the cloth on it never change, and the wheel is drawn over
   * the top of it either way.
   */
  const buildFelt = () => {
    const layer = document.createElement("canvas");
    layer.width = width;
    layer.height = height;
    const paint = layer.getContext("2d");
    if (!paint) return null;

    // The fall of the light across the table: gathering up and to the left,
    // the way it does on everything else here.
    const span = Math.max(width, height);
    const light = paint.createRadialGradient(
      width * 0.3,
      height * 0.18,
      0,
      width * 0.5,
      height * 0.5,
      span,
    );
    light.addColorStop(0, css(PALETTE.felt));
    light.addColorStop(1, css(PALETTE.feltDeep));
    paint.fillStyle = light;
    paint.fillRect(0, 0, width, height);

    // The nap: cloth is brushed, so the light pools in patches far broader
    // than the weave and the green is never quite the same twice.
    let seed = 0x2545f491;
    const random = () => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 4294967296;
    };
    for (let i = 0; i < 70; i++) {
      const x = random() * width;
      const y = random() * height;
      const r = (0.06 + 0.14 * random()) * span;
      const patch = paint.createRadialGradient(x, y, 0, x, y, r);
      patch.addColorStop(0, random() > 0.5 ? "rgba(255, 252, 232, 0.055)" : "rgba(0, 18, 8, 0.07)");
      patch.addColorStop(1, "rgba(0, 0, 0, 0)");
      paint.fillStyle = patch;
      paint.beginPath();
      paint.arc(x, y, r, 0, TAU);
      paint.fill();
    }

    const tile = buildFeltTile();
    const pattern = tile && paint.createPattern(tile, "repeat");
    if (pattern) {
      paint.fillStyle = pattern;
      paint.fillRect(0, 0, width, height);
    }
    return layer;
  };

  const drawBody = () => {
    const rimTop = D.bowlSurfaceY(D.R_RIM);
    const rimOuter = D.R_BODY;
    const ringOuter = D.R_BAND_OUT + D.RING_WIDTH;

    // The body, seen as the side of the wheel: the same outline drawn once at
    // the height of the table and once at the rim, with the gap between them
    // filled in.
    ctx.fillStyle = css(PALETTE.rosewoodDeep);
    ctx.beginPath();
    arc(rimOuter, D.BASE_Y, 0, TAU);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    arc(rimOuter, D.BASE_Y, 0, Math.PI);
    arc(rimOuter, rimTop - 0.03, Math.PI, 0, true);
    ctx.closePath();
    ctx.fill();

    // The turned crown of the rim, and the dish falling away inside it to the
    // ball track. Rosewood the whole way: the only metal above the pockets is
    // the ring at the bottom of the bowl and the diamonds set into the wall.
    ctx.fillStyle = litFill(rimOuter, rimTop, PALETTE.rosewoodLit, PALETTE.rosewoodDeep);
    ring(rimOuter, rimTop);
    ctx.fill();
    ctx.fillStyle = litFill(D.R_RIM, rimTop, PALETTE.rosewood, PALETTE.rosewoodDeep);
    ring(D.R_RIM, rimTop);
    ctx.fill();
    ctx.fillStyle = litFill(
      D.R_TRACK,
      D.bowlSurfaceY(D.R_TRACK),
      mix(PALETTE.rosewood, PALETTE.rosewoodLit, 0.25),
      PALETTE.rosewoodDeep,
    );
    ring(D.R_TRACK, D.bowlSurfaceY(D.R_TRACK));
    ctx.fill();
    grain(rimOuter, rimTop, ringOuter, D.bowlSurfaceY(ringOuter));

    // The brass ring the number band runs against.
    ctx.fillStyle = litFill(ringOuter, D.bowlSurfaceY(ringOuter), PALETTE.brassLit, PALETTE.brass);
    ring(ringOuter, D.bowlSurfaceY(ringOuter));
    ctx.fill();

    // The diamonds, drawn the way the 3D one builds them: a lozenge lying in
    // the dish with its long axis pointing at the middle of the wheel, and the
    // seam of the inlay round it. Its corners are projected rather than being
    // stepped off in screen space, so it lies in the wheel like everything
    // else rather than standing up out of it.
    for (const deflector of D.DEFLECTORS) {
      const { angle: a, radius: r, turned } = deflector;
      const long = D.DEFLECTOR_SIZE * (turned ? 0.62 : 1);
      const wide = D.DEFLECTOR_SIZE * (turned ? 1 : 0.62);
      const lozenge = (spread: number, lift: number) => {
        const corners: Array<[number, number]> = [
          [r - long * spread, 0],
          [r, -wide * spread],
          [r + long * spread, 0],
          [r, wide * spread],
        ];
        ctx.beginPath();
        corners.forEach(([radius, offset], i) => {
          const [x, y] = project(radius, a + offset / r, D.bowlSurfaceY(radius) + lift);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.closePath();
      };

      ctx.fillStyle = css(PALETTE.brassDeep);
      lozenge(1.3, 0.001);
      ctx.fill();

      const [x, y] = project(r, a, D.bowlSurfaceY(r));
      const size = scale * long;
      const gradient = ctx.createLinearGradient(x - size, y - size, x + size, y + size);
      gradient.addColorStop(0, css(PALETTE.brassLit));
      gradient.addColorStop(1, css(PALETTE.brass));
      ctx.fillStyle = gradient;
      lozenge(1, 0.006);
      ctx.fill();
    }
  };

  const drawPockets = (state: SceneState) => {
    const step = TAU / SLOT_COUNT;
    const floorCentre = centreAt(D.POCKET_FLOOR_Y);
    // The lip, not the fret line: the pockets are open on their outer side,
    // and what stands there is the low wall a settled ball rests against.
    const lipCentre = centreAt(D.POCKET_LIP_Y);
    const bandY = D.bowlSurfaceY(D.R_BAND_OUT);

    for (let slot = 0; slot < SLOT_COUNT; slot++) {
      const from = slot * step + state.wheelAngle;
      const to = from + step;
      const paint = POCKET_PAINT[pocketTone(slot, layout)];
      const winner = slot === state.highlight;
      const glow = (colour: Rgb) =>
        winner ? mix(colour, PALETTE.gold, 0.45 * state.glow) : colour;

      // The number band: the sloping ring outside the pockets, in the field's
      // own colour. It lies on the bowl's curve, so it is drawn between two
      // circles at two different heights rather than as a flat annulus.
      ctx.fillStyle = css(glow(mix(paint.face, PALETTE.bone, 0.045)));
      ctx.beginPath();
      ctx.ellipse(cx, centreAt(bandY), scale * D.R_BAND_OUT, scale * D.R_BAND_OUT * SIN, 0, from, to);
      ctx.ellipse(cx, lipCentre, scale * D.R_POCKET_OUT, scale * D.R_POCKET_OUT * SIN, 0, to, from, true);
      ctx.closePath();
      ctx.fill();

      // The pocket's outer wall, but only on the half of the wheel whose walls
      // face the viewer — on the far half they are behind their own floor.
      const mid = from + step / 2;
      if (Math.sin(mid) > -0.15) {
        ctx.fillStyle = css(glow(paint.deep));
        ctx.beginPath();
        ctx.ellipse(cx, lipCentre, scale * D.R_POCKET_OUT, scale * D.R_POCKET_OUT * SIN, 0, from, to);
        ctx.ellipse(
          cx,
          floorCentre,
          scale * D.R_POCKET_OUT,
          scale * D.R_POCKET_OUT * SIN,
          0,
          to,
          from,
          true,
        );
        ctx.closePath();
        ctx.fill();
      }

      // The floor, deepening towards the middle of the wheel the way it does
      // under the lean of the cone.
      const floor = ctx.createRadialGradient(
        cx,
        floorCentre,
        scale * D.R_POCKET_IN,
        cx,
        floorCentre,
        scale * D.R_POCKET_OUT,
      );
      floor.addColorStop(0, css(glow(mix(paint.face, paint.deep, 0.42))));
      floor.addColorStop(1, css(glow(paint.face)));
      ctx.fillStyle = floor;
      ctx.beginPath();
      ctx.ellipse(cx, floorCentre, scale * D.R_POCKET_OUT, scale * D.R_POCKET_OUT * SIN, 0, from, to);
      ctx.ellipse(cx, floorCentre, scale * D.R_POCKET_IN, scale * D.R_POCKET_IN * SIN, 0, to, from, true);
      ctx.closePath();
      ctx.fill();
    }

    // The frets: steel blades between the pockets, running from the foot of
    // the cone out across the number band. Two strokes each — the body of the
    // blade, and the lit top edge that is most of what you see of it.
    for (const [colour, wide] of [
      [PALETTE.silverDeep, 0.016],
      [PALETTE.silver, 0.007],
    ] as Array<[Rgb, number]>) {
      ctx.strokeStyle = css(colour);
      ctx.lineWidth = Math.max(1, scale * wide);
      ctx.lineCap = "round";
      for (let slot = 0; slot < SLOT_COUNT; slot++) {
        const a = slot * step + state.wheelAngle;
        const inner = project(D.FRET_INNER_R, a, D.coneSurfaceY(D.FRET_INNER_R));
        const outer = project(D.FRET_OUTER_R, a, D.FRET_TOP_Y);
        ctx.beginPath();
        ctx.moveTo(inner[0], inner[1]);
        ctx.lineTo(outer[0], outer[1]);
        ctx.stroke();
      }
    }
  };

  const drawCone = (state: SceneState) => {
    // The cone rises towards the middle, so its silhouette is the pocket ring's
    // inner circle lifted by however tall it is.
    const rise = scale * (D.CONE_TOP_Y - D.FRET_TOP_Y) * COS;
    const centre = centreAt(D.FRET_TOP_Y) - rise * 0.35;
    const radiusX = scale * D.R_POCKET_IN;
    const radiusY = scale * D.R_POCKET_IN * SIN + rise * 0.35;

    ctx.save();
    ctx.beginPath();
    ctx.ellipse(cx, centre, radiusX, radiusY, 0, 0, TAU);
    ctx.closePath();
    ctx.clip();
    const cone = ctx.createRadialGradient(
      cx - radiusX * 0.3,
      centre - radiusY * 0.45,
      radiusX * 0.05,
      cx,
      centre,
      radiusX * 1.2,
    );
    cone.addColorStop(0, css(PALETTE.rosewood));
    cone.addColorStop(1, css(PALETTE.rosewoodDeep));
    ctx.fillStyle = cone;
    ctx.fillRect(cx - radiusX, centre - radiusY, radiusX * 2, radiusY * 2);

    // The fret lines carried on up the cone as inlaid strips.
    ctx.strokeStyle = css(PALETTE.silver, 0.5);
    ctx.lineWidth = Math.max(1, scale * 0.005);
    for (let slot = 0; slot < SLOT_COUNT; slot++) {
      const a = slot * (TAU / SLOT_COUNT) + state.wheelAngle;
      // From the foot of the turret outwards, not from the middle: 22 lines
      // meeting at a point is a starburst, and there is a brass hub there.
      ctx.beginPath();
      ctx.moveTo(cx + radiusX * 0.16 * Math.cos(a), centre + radiusY * 0.16 * Math.sin(a));
      ctx.lineTo(cx + radiusX * Math.cos(a), centre + radiusY * Math.sin(a));
      ctx.stroke();
    }
    ctx.restore();

    // The brass turret, and the spindle handle across the top of it.
    const handle = D.HANDLE_Y;
    ctx.strokeStyle = css(PALETTE.brass);
    ctx.lineWidth = Math.max(2, scale * D.HANDLE_THICKNESS * 1.6);
    ctx.lineCap = "round";
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU + Math.PI / 4 + state.wheelAngle;
      const [hx, hy] = project(0, 0, handle);
      const [tx, ty] = project(D.HANDLE_ARM, a, handle - 0.022);
      ctx.beginPath();
      ctx.moveTo(hx, hy);
      ctx.lineTo(tx, ty);
      ctx.stroke();
      ctx.fillStyle = css(PALETTE.brassLit);
      ctx.beginPath();
      ctx.arc(tx, ty, Math.max(2, scale * D.HANDLE_THICKNESS * 1.35), 0, TAU);
      ctx.fill();
    }
    const [sx, sy] = project(0, 0, D.TURRET_TOP_Y);
    ctx.fillStyle = litFill(0.06, D.TURRET_TOP_Y, PALETTE.brassLit, PALETTE.brass);
    ctx.beginPath();
    ctx.ellipse(sx, sy + scale * 0.03, scale * 0.05, scale * 0.075, 0, 0, TAU);
    ctx.fill();
  };

  const drawChipsAndBall = (state: SceneState) => {
    interface Drawable {
      depth: number;
      draw: () => void;
    }
    const items: Drawable[] = [];
    // Everything below is drawn after the loop that builds it, so the size the
    // wheel is being drawn at is taken once here rather than read out of a
    // long-lived variable from inside a closure.
    const px = scale;

    for (let slot = 0; slot < SLOT_COUNT; slot++) {
      const angle = slotAngle(slot) + state.wheelAngle;
      const winner = slot === state.highlight;
      const grow = winner ? state.highlightScale : 1;
      const image = chips[layout.slots[slot]];
      const [x, y] = project(D.CHIP_RADIUS, angle, D.CHIP_Y);
      const size = D.CHIP_SIZE * grow;
      // The plate lies in the wheel, so it is drawn under the same flattening
      // as the wheel: its two axes are projected and handed to the context as
      // a transform, which turns and squashes the picture exactly as much as
      // the pocket it sits in.
      const across: [number, number] = [px * Math.sin(angle), -px * Math.cos(angle) * SIN];
      const outward: [number, number] = [px * Math.cos(angle), px * Math.sin(angle) * SIN];
      items.push({
        depth: Math.sin(angle),
        draw: () => {
          if (winner && state.glow > 0) {
            // Drawn in screen space, before the plate and under it: a blur set
            // inside the transform below would be stretched by it too.
            const halo = px * D.CHIP_SIZE * 1.1;
            const glow = ctx.createRadialGradient(x, y, halo * 0.3, x, y, halo);
            glow.addColorStop(0, css(PALETTE.gold, 0.75 * state.glow));
            glow.addColorStop(1, css(PALETTE.gold, 0));
            ctx.fillStyle = glow;
            ctx.beginPath();
            ctx.ellipse(x, y, halo, halo * SIN, 0, 0, TAU);
            ctx.fill();
          }
          ctx.save();
          ctx.transform(across[0], across[1], outward[0], outward[1], x, y);
          ctx.drawImage(image, -size / 2, -size / 2, size, size);
          ctx.restore();
        },
      });
    }

    if (state.ballVisible) {
      const { angle, radius, y } = state.ball;
      const [bx, by] = project(radius, angle, y);
      const r = px * D.BALL_RADIUS;
      items.push({
        depth: Math.sin(angle) + 0.001,
        draw: () => {
          const gradient = ctx.createRadialGradient(bx - r * 0.35, by - r * 0.4, r * 0.1, bx, by, r);
          gradient.addColorStop(0, "#ffffff");
          gradient.addColorStop(1, css(mix(PALETTE.bone, PALETTE.ink3, 0.45)));
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(bx, by, r, 0, TAU);
          ctx.fill();
        },
      });
    }

    items.sort((a, b) => a.depth - b.depth);
    for (const item of items) item.draw();
  };

  return {
    kind: "canvas",

    resize(cssWidth, cssHeight, dpr) {
      width = Math.max(1, Math.round(cssWidth * dpr));
      height = Math.max(1, Math.round(cssHeight * dpr));
      canvas.width = width;
      canvas.height = height;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      // The perspective camera's scale is measured at the wheel's own plane;
      // an orthographic drawing has no near-end enlargement to give away, so
      // it is pulled in a little to leave the same margin round the rim.
      scale = cameraFor(width / height).scale(height) * 0.94;
      cx = width / 2;
      // Lifted off centre: the body hangs below the wheel's plane, so
      // centring the plane itself leaves the drawing sitting low in its box.
      cy = height * 0.48;
      feltLayer = buildFelt();
    },

    render(state) {
      if (!width || !height) return;
      ctx.save();
      if (feltLayer) {
        ctx.drawImage(feltLayer, 0, 0);
      } else {
        ctx.fillStyle = css(PALETTE.feltDeep);
        ctx.fillRect(0, 0, width, height);
      }

      // The wheel's own shadow on the cloth: its silhouette pushed along the
      // key light, the same throw the 3D wheel works out, rather than a pool
      // under the middle of it. Two passes — the shadow proper, thrown to one
      // side, and the close contact all round the foot, which is there
      // whichever way the light comes from.
      //
      // Gradients rather than filled ellipses: a hard edge round the wheel
      // reads as a second, darker ring of the wheel itself rather than as
      // shade. And in a cool grey rather than black — the key light is warm,
      // so what is left in the shadow is the blue fill.
      const baseY = centreAt(D.BASE_Y);
      const throwX = -(KEY_LIGHT[0] / KEY_LIGHT[1]) * WHEEL_HEIGHT;
      const throwZ = -(KEY_LIGHT[2] / KEY_LIGHT[1]) * WHEEL_HEIGHT;
      for (const [x, y, r, alpha] of [
        [cx + scale * throwX * 0.75, baseY + scale * throwZ * SIN * 0.75, D.R_BODY + 0.16, 0.46],
        [cx, baseY + scale * 0.04, D.R_BODY + 0.18, 0.42],
      ] as Array<[number, number, number, number]>) {
        const radius = scale * r;
        const shade = ctx.createRadialGradient(x, y, radius * 0.62, x, y, radius);
        shade.addColorStop(0, `rgba(2, 12, 10, ${alpha})`);
        shade.addColorStop(1, "rgba(2, 12, 10, 0)");
        ctx.fillStyle = shade;
        ctx.beginPath();
        ctx.ellipse(x, y, radius, radius * SIN, 0, 0, TAU);
        ctx.fill();
      }

      drawBody();
      drawPockets(state);
      drawCone(state);
      drawChipsAndBall(state);
      ctx.restore();
    },

    destroy() {
      /* nothing to release: the 2D context owns no GPU objects of ours */
    },
  };
}
